import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import { Event, getCategoryLabel, getCategoryColor } from '../../types/event';
import { formatDate, formatTime, getSpotsRemaining, isEventFull } from '../../utils/helpers';

interface EventCardProps {
  event: Event;
  onView: (eventId: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onView }) => {
  const spotsRemaining = getSpotsRemaining(event.capacity, event.currentAttendees);
  const isFull = isEventFull(event.capacity, event.currentAttendees);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box sx={{ height: 8, backgroundColor: getCategoryColor(event.category) }} />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Chip
            label={getCategoryLabel(event.category)}
            size="small"
            sx={{ backgroundColor: getCategoryColor(event.category), color: 'white' }}
          />
          {isFull ? (
            <Chip label="FULL" size="small" color="error" />
          ) : spotsRemaining <= 10 ? (
            <Chip label={`${spotsRemaining} spots left`} size="small" color="warning" />
          ) : null}
        </Box>

        <Typography variant="h6" component="h2" gutterBottom>
          {event.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {event.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EventIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {formatDate(event.date)} at {formatTime(event.startTime)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {event.location}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button variant="contained" fullWidth onClick={() => onView(event.id)} disabled={isFull}>
          {isFull ? 'Event Full' : 'View Details'}
        </Button>
      </CardActions>
    </Card>
  );
};

export default EventCard;
